/**
 * ============================================================================
 * ActivityFeed.tsx - ACTIVITY FEED COMPONENT
 * ============================================================================
 * 
 * Displays real-time activity feed showing wins, big hands, and social updates.
 * Can be used as a sidebar widget or full-page component.
 * 
 * ============================================================================
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Activity, ChevronRight, Sparkles, TrendingUp, RefreshCw } from 'lucide-react';
import activityFeedService, {
  ActivityItem,
  ActivityType,
  formatActivityForDisplay,
} from '../services/activityFeedService';
import './ActivityFeed.css';

// ============================================================================
// TYPES
// ============================================================================

interface ActivityFeedProps {
  maxItems?: number;
  compact?: boolean;
  showHeader?: boolean;
  showViewAll?: boolean;
  types?: ActivityType[];
  currency?: 'playcoins' | 'pkcash' | 'all';
  autoScroll?: boolean;
  onViewAll?: () => void;
}

// ============================================================================
// SINGLE ACTIVITY ITEM
// ============================================================================

interface ActivityItemRowProps {
  activity: ActivityItem;
  compact?: boolean;
  onClick?: () => void;
}

const ActivityItemRow: React.FC<ActivityItemRowProps> = ({
  activity,
  compact = false,
  onClick,
}) => {
  const formatted = formatActivityForDisplay(activity);
  
  return (
    <div 
      className={`activity-item ${compact ? 'compact' : ''} ${onClick ? 'clickable' : ''}`}
      onClick={onClick}
      style={{ '--activity-color': formatted.color } as React.CSSProperties}
    >
      {/* Emoji */}
      <div className="activity-emoji">
        {formatted.emoji}
      </div>
      
      {/* Content */}
      <div className="activity-content">
        <div className="activity-text">
          <span className="activity-username">{formatted.username}</span>
          {' '}
          <span className="activity-message">{formatted.message}</span>
        </div>
        <div className="activity-meta">
          <span className="activity-time">{formatted.timeAgo}</span>
          {activity.tableName && !compact && (
            <>
              <span className="activity-dot">•</span>
              <span className="activity-table">{activity.tableName}</span>
            </>
          )}
        </div>
      </div>
      
      {/* Currency indicator */}
      {activity.currency && (
        <div className={`activity-currency ${activity.currency}`}>
          {activity.currency === 'pkcash' ? '' : ''}

        </div>
      )}
    </div>
  );
};

// ============================================================================
// ACTIVITY FEED COMPONENT
// ============================================================================

const ActivityFeed: React.FC<ActivityFeedProps> = ({
  maxItems = 10,
  compact = false,
  showHeader = true,
  showViewAll = true,
  types,
  currency = 'all',
  autoScroll = true,
  onViewAll,
}) => {
  const navigate = useNavigate();
  const [activities, setActivities] = useState<ActivityItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [isLive, setIsLive] = useState(true);
  const [expanded, setExpanded] = useState(false);

  // Subscribe to activity feed
  useEffect(() => {
    setLoading(true);
    const fetchCount = expanded ? 50 : maxItems;

    const unsubscribe = activityFeedService.subscribeToActivityFeed(
      (items) => {
        setActivities(items);
        setLoading(false);
      },
      { maxItems: fetchCount, types, currency }
    );

    return () => unsubscribe();
  }, [maxItems, types, currency, expanded]);

  // Handle activity click (navigate to table if available)
  const handleActivityClick = useCallback((activity: ActivityItem) => {
    if (activity.tableId) {
      navigate(`/game/${activity.tableId}`);
    }
  }, [navigate]);

  // Handle view all - expand inline instead of navigating
  const handleViewAll = useCallback(() => {
    if (onViewAll) {
      onViewAll();
    } else {
      setExpanded(prev => !prev);
    }
  }, [onViewAll]);

  // Refresh feed
  const handleRefresh = useCallback(async () => {
    setLoading(true);
    const items = await activityFeedService.getRecentActivities({ maxItems });
    setActivities(items);
    setLoading(false);
  }, [maxItems]);

  return (
    <div className={`activity-feed ${compact ? 'compact' : ''}`}>
      {/* Header */}
      {showHeader && (
        <div className="activity-feed-header">
          <div className="activity-feed-title">
            <Activity size={18} className="activity-feed-icon" />
            <span>Live Activity</span>
            {isLive && (
              <span className="live-indicator">
                <span className="live-dot" />
                LIVE
              </span>
            )}
          </div>
          <button 
            className="activity-refresh-btn"
            onClick={handleRefresh}
            disabled={loading}
          >
            <RefreshCw size={16} className={loading ? 'spinning' : ''} />
          </button>
        </div>
      )}

      {/* Activity List */}
      <div className={`activity-list ${autoScroll ? 'auto-scroll' : ''}`}>
        {loading && activities.length === 0 ? (
          // Loading skeleton
          <div className="activity-loading">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="activity-skeleton">
                <div className="skeleton-emoji" />
                <div className="skeleton-content">
                  <div className="skeleton-line long" />
                  <div className="skeleton-line short" />
                </div>
              </div>
            ))}
          </div>
        ) : activities.length === 0 ? (
          // Empty state
          <div className="activity-empty">
            <Sparkles size={32} />
            <p>No recent activity</p>
            <span>Big wins and notable hands will appear here!</span>
          </div>
        ) : (
          // Activity items
          activities.map((activity, index) => (
            <ActivityItemRow
              key={activity.id}
              activity={activity}
              compact={compact}
              onClick={activity.tableId ? () => handleActivityClick(activity) : undefined}
            />
          ))
        )}
      </div>

      {/* View All Link */}
      {showViewAll && activities.length > 0 && (
        <button className="activity-view-all" onClick={handleViewAll}>
          <TrendingUp size={16} />
          <span>{expanded ? 'Show Less' : 'View All Activity'}</span>
          <ChevronRight size={16} style={expanded ? { transform: 'rotate(90deg)' } : undefined} />
        </button>
      )}
    </div>
  );
};

// ============================================================================
// MINI ACTIVITY TICKER (For navbar/header)
// ============================================================================

interface ActivityTickerProps {
  maxItems?: number;
}

export const ActivityTicker: React.FC<ActivityTickerProps> = ({ maxItems = 5 }) => {
  const [activities, setActivities] = useState<ActivityItem[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);

  // Subscribe to feed
  useEffect(() => {
    const unsubscribe = activityFeedService.subscribeToActivityFeed(
      (items) => setActivities(items),
      { maxItems }
    );
    return () => unsubscribe();
  }, [maxItems]);

  // Rotate through activities
  useEffect(() => {
    if (activities.length <= 1) return;
    
    const interval = setInterval(() => {
      setCurrentIndex(prev => (prev + 1) % activities.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [activities.length]);

  if (activities.length === 0) return null;

  const current = activities[currentIndex];
  const formatted = formatActivityForDisplay(current);

  return (
    <div className="activity-ticker">
      <span className="ticker-emoji">{formatted.emoji}</span>
      <span className="ticker-text">
        <strong>{formatted.username}</strong> {formatted.message}
      </span>
    </div>
  );
};

export default ActivityFeed;